import moment from 'moment'
import { FunctionComponent, useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import Masonry from 'react-masonry-css'
import { Clip } from '../../../../gql/types'
import { ClipTypeBadge } from '../../../../components/ClipType'
import { VideoZoom } from '../../../../components/VideoZoom'
import { Avatar } from '../../../../elements'
import {
  Badge,
  BadgeType,
  BadgeStyle,
  BadgeSize,
} from '../../../../components/Badge'

interface IProps {
  clips: Clip[]
  loading: boolean
  labelNames?: string[]
}

const ContentGridPublic: FunctionComponent<IProps> = ({
  clips,
  loading,
  labelNames = [],
}) => {
  const [numCols, setNumCols] = useState(6)
  const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null)

  const getNumColumns = (width: number) => {
    if (width >= 1536) return 6
    else if (width >= 1280) return 5
    else if (width >= 1024) return 4
    else if (width >= 768) return 3
    else return 1
  }

  useEffect(() => {
    if (!gridElement) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const newNumCols = getNumColumns(entry.contentRect.width)
        setNumCols(newNumCols)
      }
    })

    resizeObserver.observe(gridElement)

    return () => {
      resizeObserver.disconnect()
    }
  }, [gridElement])

  if (!loading && clips?.length === 0) {
    return (
      <div className='lg:max-w-7xl'>
        <p className='text-sm text-gray-700 px-4 py-2'>No clips found.</p>
      </div>
    )
  }

  return (
    <div ref={setGridElement}>
      <Masonry
        breakpointCols={numCols}
        className='flex p-3 -ml-2 md:-ml-2 lg:-ml-4 xl:-ml-4 2xl:-ml-6'
        columnClassName='pl-2 space-y-2 md:pl-2 md:space-y-4 lg:pl-4 lg:space-y-4 xl:pl-4 xl:space-y-4 2xl:pl-6 2xl:space-y-6'
      >
        {clips?.map((clip: Clip) => (
          <div key={clip.id}>
            <div className='shadow-2xl overflow-hidden rounded-xl ring-1 ring-black ring-opacity-20 w-full relative'>
              <VideoZoom
                src={clip?.video?.url}
                thumbnailSrc={clip?.smallThumbnail?.url ?? clip?.thumbnail?.url}
                className='cursor-pointer rounded-xl w-full object-cover hover:opacity-75'
              />
              <div className='absolute left-2 top-2 gap-y-1 overflow-hidden text-sm leading-6 text-white font-bold bg-black/30 px-1 rounded-lg'>
                <div className='flex gap-x-1 items-center'>
                  <Avatar
                    type='user'
                    size='xxs'
                    src={clip.creator.avatar?.url}
                    alt={clip.creator?.firstName}
                  />
                  <div className=''>
                    {clip.creator?.firstName}
                    <div className='text-xs pb-1'>
                      {moment(clip.createdAt).format('ll')}
                    </div>
                  </div>
                </div>
              </div>

              {clip?.type && (
                <div className='absolute left-2 top-12 pt-1 overflow-hidden'>
                  <ClipTypeBadge
                    type={clip.type}
                    aspectRatio={clip.campaign.aspectRatio}
                    addon={clip?.addon}
                  />
                </div>
              )}

              {labelNames.length > 0 && clip.clipLabels && (
                <div className='absolute bottom-2 left-2 flex flex-wrap gap-1 justify-start'>
                  {clip.clipLabels
                    .filter((clipLabel) =>
                      labelNames.includes(clipLabel.label.name),
                    )
                    .map((clipLabel, idx) => (
                      <Badge
                        key={idx}
                        type={BadgeType.gray}
                        style={BadgeStyle.fill}
                        size={BadgeSize.small}
                        text={clipLabel.label.name}
                      />
                    ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading &&
          [...Array(10)].map((x, i) => (
            <div key={i}>
              <Skeleton className='shadow-2xl overflow-hidden !rounded-xl ring-1 ring-black ring-opacity-20 w-full relative aspect-[9/16]' />
            </div>
          ))}
      </Masonry>
    </div>
  )
}

export { ContentGridPublic }
